import type { LoadedFile, SourceFormat } from '../types.js';

function detectFormat(file: File): SourceFormat | null {
  const type = file.type;
  const name = file.name.toLowerCase();
  if (type === 'image/png' || name.endsWith('.png')) return 'png';
  if (type === 'image/jpeg' || name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'jpeg';
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  return null;
}

let nextId = 1;

export class FileManager {
  private files: LoadedFile[] = [];
  private activeId: string | null = null;
  onChange: (files: LoadedFile[], activeId: string | null) => void = () => {};

  getFiles(): LoadedFile[] {
    return this.files;
  }

  getActive(): LoadedFile | null {
    return this.files.find((f) => f.id === this.activeId) ?? null;
  }

  setActive(id: string): void {
    this.activeId = id;
    this.onChange(this.files, this.activeId);
  }

  async addFiles(fileList: FileList | File[]): Promise<void> {
    const rejected: string[] = [];
    for (const file of Array.from(fileList)) {
      const format = detectFormat(file);
      if (!format) {
        rejected.push(file.name);
        continue;
      }
      const bytes = await file.arrayBuffer();
      const loaded: LoadedFile = { id: `f${nextId++}`, name: file.name, sourceFormat: format, originalBytes: bytes };
      this.files.push(loaded);
      if (!this.activeId) this.activeId = loaded.id;
    }
    this.onChange(this.files, this.activeId);
    if (rejected.length) {
      throw new Error(`Unsupported file type: ${rejected.join(', ')} (only PNG, JPEG, and PDF are supported)`);
    }
  }

  remove(id: string): void {
    this.files = this.files.filter((f) => f.id !== id);
    if (this.activeId === id) {
      this.activeId = this.files[0]?.id ?? null;
    }
    this.onChange(this.files, this.activeId);
  }

  clear(): void {
    this.files = [];
    this.activeId = null;
    this.onChange(this.files, this.activeId);
  }
}

export function setupDropzone(el: HTMLElement, onFiles: (files: FileList) => void): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.multiple = true;
  input.accept = '.png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf';
  input.className = 'visually-hidden';
  input.addEventListener('change', () => {
    if (input.files && input.files.length) onFiles(input.files);
    input.value = '';
  });
  el.appendChild(input);

  el.addEventListener('click', () => input.click());
  el.setAttribute('role', 'button');
  el.setAttribute('tabindex', '0');
  el.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      input.click();
    }
  });

  el.addEventListener('dragover', (e) => {
    e.preventDefault();
    el.classList.add('dragover');
  });
  el.addEventListener('dragleave', () => el.classList.remove('dragover'));
  el.addEventListener('drop', (e) => {
    e.preventDefault();
    el.classList.remove('dragover');
    if (e.dataTransfer?.files.length) onFiles(e.dataTransfer.files);
  });
}

export function renderFileList(listEl: HTMLUListElement, files: LoadedFile[], activeId: string | null, onSelect: (id: string) => void, onRemove: (id: string) => void): void {
  listEl.innerHTML = '';
  for (const file of files) {
    const li = document.createElement('li');
    li.className = file.id === activeId ? 'active' : '';
    const nameSpan = document.createElement('span');
    nameSpan.textContent = file.name;
    nameSpan.style.overflow = 'hidden';
    nameSpan.style.textOverflow = 'ellipsis';
    nameSpan.style.whiteSpace = 'nowrap';
    nameSpan.style.cursor = 'pointer';
    nameSpan.addEventListener('click', () => onSelect(file.id));
    const removeBtn = document.createElement('button');
    removeBtn.className = 'remove';
    removeBtn.textContent = '✕';
    removeBtn.setAttribute('aria-label', `Remove ${file.name}`);
    removeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      onRemove(file.id);
    });
    li.appendChild(nameSpan);
    li.appendChild(removeBtn);
    listEl.appendChild(li);
  }
}
